import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { FiExternalLink } from "react-icons/fi";
import utils from "../../helpers/utils";
import feedback from "../../services/feedback";
import linksthree from "../../services/linksthree";
import { LinkThreeData, LinkButton } from "../../types";
import LinkThreeScheduleModal from "../../components/links/LinkThreeScheduleModal";

function LinkThree() {
  const { id } = useParams();
  const [data, setData] = useState<LinkThreeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [activeScheduleButton, setActiveScheduleButton] =
    useState<LinkButton | null>(null);

  const loadData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const result = await linksthree.getButtons(id);
        setData(result);
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [id, loadData]);

  const sendFeedback = async (btid: string) => {
    if (id) {
      await feedback.send_acess(id, btid);
    }
  };

  const handleButtonClick = async (btid: string, url: string) => {
    sendFeedback(btid);

    const finalUrl = url.indexOf("http") !== -1 ? url : `https://${url}`;
    window.open(finalUrl, "_blank");
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center py-10 px-4 bg-slate-900">
        <div className="w-full max-w-2xl animate-pulse">
          {/* Header Skeleton */}
          <div className="mb-8 flex flex-col items-center gap-4 text-center">
            {/* Avatar Skeleton */}
            <div className="h-28 w-28 rounded-full bg-slate-800 border-4 border-slate-700"></div>

            {/* Name and Description Skeleton */}
            <div className="space-y-3 w-full flex flex-col items-center">
              <div className="h-8 w-48 rounded bg-slate-800"></div>
              <div className="h-4 w-64 rounded bg-slate-800"></div>
            </div>
          </div>

          {/* Buttons Skeleton */}
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-20 w-full rounded-xl bg-slate-800 p-4 shadow-sm"
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900">
        <div className="text-center">
          <p className="text-xl font-semibold text-gray-600 dark:text-gray-400">
            Página não encontrada
          </p>
        </div>
      </div>
    );
  }

  const pageStyle = {
    backgroundColor: data.background || "#0F172A",
    minHeight: "100vh",
  };

  const visibleButtons: LinkButton[] = (data.buttons || []).filter((button) =>
    button.is_schedule ? button.enabled !== false : true,
  );

  return (
    <div
      className="flex items-center justify-center py-10 px-4"
      style={pageStyle}
    >
      <div className="w-full max-w-2xl">
        {/* Header - Profile Section */}
        <div className="mb-8 flex flex-col items-center gap-4 text-center">
          {/* Avatar */}
          {data.photo && (
            <div className="relative">
              <div className="h-28 w-28 overflow-hidden rounded-full border-4 border-white shadow-xl">
                <img
                  src={utils.getUriImage(data.photo)}
                  alt={data.name || "Profile"}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-2 -right-2 rounded-full bg-green-500 p-2 shadow-sm">
                <div className="h-3 w-3 rounded-full bg-white"></div>
              </div>
            </div>
          )}

          {/* Name and Description */}
          <div className="space-y-2">
            {data.name && (
              <h1
                className="text-3xl font-bold"
                style={{ color: data.color_secondary || "#FFFFFF" }}
              >
                {data.name}
              </h1>
            )}
            {data.description && (
              <p
                className="text-base font-medium opacity-90"
                style={{ color: data.color_secondary || "#E2E8F0" }}
              >
                {data.description}
              </p>
            )}
          </div>
        </div>

        {/* Buttons Section */}
        <div className="space-y-3">
          {visibleButtons && visibleButtons.length > 0 ? (
            visibleButtons.map((button: LinkButton) => {
              const isSchedule = button.is_schedule && button.enabled !== false;

              return (
                <button
                  key={button.id}
                  onClick={() => {
                    if (isSchedule) {
                      sendFeedback(button.id);
                      setActiveScheduleButton(button);
                      setScheduleModalOpen(true);
                    } else {
                      handleButtonClick(button.id, button.url);
                    }
                  }}
                  className="group relative w-full h-20 overflow-hidden rounded-xl p-4 shadow-sm transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                  style={{
                    backgroundColor: data.color || "#1E293B",
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
                  <div className="relative flex items-center justify-between">
                    <div className="flex-shrink-0 z-10">
                      <div className="h-14 w-14 overflow-hidden rounded-lg p-1">
                        {button.image && (
                          <img
                            src={utils.getUriImage(button.image)}
                            alt={button.label}
                            className="h-full w-full rounded-md object-contain"
                          />
                        )}
                      </div>
                    </div>
                    <div className="absolute inset-x-0 flex justify-center">
                      <span
                        className="text-lg font-semibold"
                        style={{
                          color: data.color_secondary || "#FFFFFF",
                        }}
                      >
                        {button.label}
                      </span>
                    </div>
                    <div className="flex-shrink-0 z-10">
                      <FiExternalLink
                        size={20}
                        style={{
                          color: data.color_secondary || "#FFFFFF",
                        }}
                        className="opacity-70 transition-opacity group-hover:opacity-100"
                      />
                    </div>
                  </div>
                  <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full"></div>
                </button>
              );
            })
          ) : (
            <div className="rounded-xl bg-white/10 p-8 text-center backdrop-blur-sm">
              <p
                className="text-lg font-medium"
                style={{ color: data.color_secondary || "#E2E8F0" }}
              >
                Nenhum link disponível no momento.
              </p>
            </div>
          )}
        </div>
      </div>
      <LinkThreeScheduleModal
        isOpen={!!(activeScheduleButton && scheduleModalOpen)}
        professionalId={id || undefined}
        professionalName={data?.name}
        primaryColor={data?.color}
        secondaryColor={data?.color_secondary}
        backgroundColor={data?.background}
        onClose={() => {
          setScheduleModalOpen(false);
          setActiveScheduleButton(null);
        }}
      />
    </div>
  );
}

export default LinkThree;
